import { Injectable } from '@angular/core';
import { FolderManagementServiceModel } from '@app/origami-api/services/folder-management.service';
import { Topic, TopicUtils } from '@app/origami-api/utils/topic-utils';
import { Observable, of } from 'rxjs';
import { TopicTypeId } from '@app/origami-api/constants/topic-type-names';
import { FieldNames } from '@app/origami-api/constants/field-names';
import { Field } from '@app/origami-api/constants/field-flag';
import { SessionService } from '@app/session/session.service';
import { TopicSearchService } from '@app/origami-api/services/topic-search.service';
import { ConfigService, ConfigServiceModel } from '@app/origami-api/services/config.service';
import moment from 'moment';
import { map, concatMap } from 'rxjs/operators';

@Injectable()
export class RelatedDocumentsService {
  constructor(
    private sessionService: SessionService,
    private topicSearchService: TopicSearchService,
    private configService: ConfigService,
  ) {}

  getRelatedDocumentsByLinkId(document: FolderManagementServiceModel.Document): Observable<Topic[]> {
    const linkId = TopicUtils.getTopicFieldValue(document.TopicFields, FieldNames.Document.LinkId);

    if (!linkId) {
      return of([]);
    }

    return this.topicSearchService
      .searchTopics({
        siteId: this.sessionService.currentRoomId,
        searchQuery: this.getSearchQueryByLinkId(document.TopicId, linkId),
        topicTypeId: TopicTypeId.Document,
      })
      .pipe(map((response) => response.Result.TopicList.filter((topic) => topic.TopicId !== document.TopicId)));
  }

  getRelatedDocuments(document: FolderManagementServiceModel.Document, documentsLimit?: number): Observable<Topic[]> {
    return this.getDocumentFormFields(document).pipe(
      map((relatedFields) =>
        relatedFields
          .filter((field) => Field.Is.includeInContact(field))
          .sort((field1, field2) => field1.SortOrder - field2.SortOrder),
      ),
      map((relatedFields) => ({
        siteId: this.sessionService.currentRoomId,
        searchQuery: this.getSearchQuery(document, relatedFields),
        topicTypeId: TopicTypeId.Document,
        limit: documentsLimit,
      })),
      concatMap((searchParams) => this.topicSearchService.searchTopics(searchParams)),
      map((response) => response.Result.TopicList.filter((topic) => topic.TopicId !== document.TopicId)),
    );
  }

  getDocumentFormFields(document: FolderManagementServiceModel.Document): Observable<ConfigServiceModel.Field[]> {
    const formIds = TopicUtils.getTopicFieldValue(document.TopicFields, FieldNames.Document.FormIds);
    const formId = formIds ? +formIds.split(',')[0] : null;

    if (!formId) {
      return of([]);
    }

    return this.configService.getCustomFieldsForm({ formId }).pipe(
      map((res) => {
        if (!res || !res.Result || !res.Result.FormFields) {
          return [];
        }
        return res.Result.FormFields;
      }),
    );
  }

  private getSearchQueryByLinkId(topicId: number, linkId: string): string {
    return `TopicAttributes.TopicId != ${topicId} AND ([${FieldNames.Document.LinkId}] = \'${linkId}\') order by [${FieldNames.Document.DocumentId}] ASC`;
  }

  private getSearchQuery(
    document: FolderManagementServiceModel.Document,
    relatedFields: ConfigServiceModel.Field[],
  ): string {
    let tmp = '';
    let manualLinkId = null;

    relatedFields.forEach((field) => {
      if (!field) {
        return;
      }

      const val = TopicUtils.getTopicFieldValue(document.TopicFields, field.FieldName);
      if (field.FieldName === FieldNames.Document.LinkId) {
        manualLinkId = val;
        return;
      }

      tmp += this.getFieldQuery(field, val, !tmp);
    });

    if (manualLinkId) {
      if (tmp) {
        tmp = `((${tmp}) OR ([${FieldNames.Document.LinkId}] = \'${manualLinkId}\'))`;
      } else {
        tmp = `([${FieldNames.Document.LinkId}] = \'${manualLinkId}\')`;
      }
    }

    if (tmp) {
      return `TopicAttributes.TopicId != ${document.TopicId} AND ${tmp} order by [${FieldNames.Document.DocumentId}] ASC`;
    }
    // to avoid full document set as a result of empty query
    return `TopicAttributes.TopicId == ${document.TopicId} order by [${FieldNames.Document.DocumentId}] ASC`;
  }

  private getFieldQuery(field: ConfigServiceModel.Field, value: any, isFirst: boolean): string {
    const optionalAnd = isFirst ? ' ' : ' AND ';

    if (value === null || value === undefined) {
      return '';
    }

    if (value !== '') {
      if (Field.Is.date(field)) {
        value = moment(new Date(Date.parse(value))).format('MM/DD/YYYY');
        return optionalAnd + ` ([${field.FieldName}] BETWEEN \'${value} 00:00:00.000\' AND \'${value} 23:59:59.997\')`;
      } else {
        value = value.toString().replace(/'/g, "''");
      }
    }
    return optionalAnd + ` [${field.FieldName}] = \'${value}\'`;
  }
}
